import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { reportSchema, IReport } from './report';
import { IPrintServer } from '../printServer/printServer';

/**
 * Schema for validating report form data.
 * Extends the base report schema with form-specific validations.
 *
 * @property {string} [name] - Report name (max 255 characters)
 * @property {OptionType} [printServer] - Print server option with label/value pair
 */
export const reportFormSchema = reportSchema.extend({
    printServer: optionTypeSchema.refine(val => val !== undefined, { message: 'Required' }),
}).omit({
    // Omit the original ID field
    printServerId: true,
});

/**
 * Default schema for report form initialization.
 * Extends the form schema by making required fields optional with defaults.
 *
 * @property {string} [name] - Optional report name
 * @property {OptionType} [printServer] - Optional print server selection
 */
export const reportDefaultFormSchema = reportFormSchema.extend({
    name: z.string().optional().default(''),
    printServer: optionTypeSchema.optional(),
});

/**
 * TypeScript interface for report default form values.
 * Generated from the report default form schema for type safety.
 */
export type IReportDefaultFormValues = z.infer<typeof reportDefaultFormSchema>;

/**
 * TypeScript interface for report form values.
 * Generated from the report form schema for type safety.
 */
export type IReportFormValues = z.infer<typeof reportFormSchema>;

/**
 * Converts a Report model to form values for editing.
 * If no report is provided, returns default form values.
 *
 * @param {IReport} [report] - Optional report model to convert
 * @param {Array<IPrintServer>} [printServers] - Array of print servers to use for mapping
 * @returns {IReportFormValues | IReportDefaultFormValues} Form values ready for editing
 */
export const ReportFormValues = (
    report ?: IReport,
    printServers ?: Array<IPrintServer>
) : IReportFormValues | IReportDefaultFormValues => {
    // Handle the default case (new report)
    if (!report) {
        return reportDefaultFormSchema.parse({});
    }

    const { printServerId, ...rest } = report;

    // Transform the IReport data into IReportFormValues structure
    return reportFormSchema.parse({
        ...rest,
        printServer: printServerId
            ? {
                label: printServers?.find(x => x.id === printServerId) ? `(${printServers.find(x => x.id === printServerId)?.name})` : '',
                value: printServerId,
            }
            : undefined,
    });
};

/**
 * Validates report form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IReportFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validateReportForm = (formValues : IReportFormValues) : boolean => {
    try {
        reportFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Report form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
